import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/shadcn/card";
import { CheckCircle, AlertCircle, ArrowDown } from "lucide-react";
import { PublicQuote, QuoteStatus } from "@/types/quotes";
import { Asset } from "@/types/assets";
import { Input } from "@/components/ui/shadcn/input";
import { Button } from "@/components/ui/shadcn/button";
import { publicQuotesAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";
import { formatAssetAmount } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface AcceptedQuoteProps {
  quote: PublicQuote;
}

// Custom hook for viewport detection
function useIsWideViewport(breakpoint = 1024) {
  const [isWide, setIsWide] = useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : false
  );
  useEffect(() => {
    function handleResize() {
      setIsWide(window.innerWidth >= breakpoint);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isWide;
}

export function AcceptedQuoteComponent({ quote }: AcceptedQuoteProps) {
  const { toast } = useToast();
  const [cryptoAddress, setCryptoAddress] = useState(
    quote?.acceptance?.crypto_address || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressSubmitted, setAddressSubmitted] = useState(
    quote?.acceptance?.crypto_address ? true : false
  );

  const acceptance_time = quote.acceptance?.accepted_at
    ? DateTime.fromISO(quote.acceptance.accepted_at, {
        zone: "America/Mexico_City",
      }).toLocaleString(DateTime.DATETIME_SHORT)
    : null;

  const isBuy = quote.side === "buy";
  const isExecuted = quote.status === QuoteStatus.EXECUTED;

  // For BUY:
  //   - total_from is always in counter (MXN)
  //   - total_to is always in base (BTC)
  // For SELL:
  //   - total_from is always in base (BTC)
  //   - total_to is always in counter (MXN)
  const fromAmount = isBuy
    ? quote.acceptance?.total_from // BUY: Give counter (MXN)
    : quote.acceptance?.total_from; // SELL: Give base (BTC)

  const toAmount = isBuy
    ? quote.acceptance?.total_to // BUY: Get base (BTC)
    : quote.acceptance?.total_to; // SELL: Get counter (MXN)

  // For BUY: fromAsset is counter (MXN), toAsset is base (BTC)
  // For SELL: fromAsset is base (BTC), toAsset is counter (MXN)
  const fromAsset = isBuy ? quote.counter : quote.base;
  const toAsset = isBuy ? quote.base : quote.counter;

  const handleAddressSubmit = async () => {
    if (!cryptoAddress || cryptoAddress === "") {
      setError(`Por favor ingrese su dirección de ${quote.base.code}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await publicQuotesAPI.addAcceptedQuoteCryptoAddress(
        quote.slug,
        cryptoAddress
      );
      showSuccessToast(toast, "Éxito", "Dirección guardada exitosamente");
      setAddressSubmitted(true);
    } catch (error) {
      handleApiError("Guardar dirección", error, toast);
      setError(
        "No se pudo guardar la dirección de criptomoneda. Por favor, intente nuevamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const showPaymentInfo = toAsset.code === "MXN";

  const showCryptoAddress =
    toAsset.asset_type === "crypto" || toAsset.asset_type === "stable" || toAsset.code === "MXN";

  const isWideViewport = useIsWideViewport(1024);
  const isTwoColumnLayout = showCryptoAddress && showPaymentInfo && !addressSubmitted && isWideViewport;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B0F1A] p-6">
      {/* BAX OTC Logo */}
      <div className="flex items-center mb-2">
        <img src="/logo_bax.png" alt="BAX Logo" className="h-10 mr-3" />
        <span className="font-[165] text-gray-400 tracking-[0.3em] text-[2.8rem] mt-[0.2rem]">
          OTC
        </span>
      </div>

      {/* Success Message Card (wide, low height) */}
      <div
        className={`w-full mb-6 mx-auto
          ${isTwoColumnLayout ? 'max-w-6xl' : 'max-w-xl'}
        `}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="w-full"
          >
            <Card className="bg-[#101522] border border-[#26C6DA]/30 shadow-xl rounded-2xl overflow-hidden px-8 py-4 flex flex-col items-center justify-center">
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 w-full">
                <CheckCircle className="h-8 w-8 md:h-10 md:w-10 text-[#26C6DA]" />
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <h2 className="text-lg md:text-2xl font-bold text-white mb-1">
                    {isExecuted
                      ? "¡Cotización ejecutada exitosamente!"
                      : "¡Cotización aceptada exitosamente!"}
                  </h2>
                  <p className="text-gray-300 text-xs md:text-base">
                    {isExecuted
                      ? "La cotización ha sido ejecutada y completada."
                      : "La cotización ha sido aceptada y será ejecutada en breve."}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Main Grid: Responsive layout based on addressSubmitted, showPaymentInfo, showCryptoAddress, and viewport */}
      <div
        className={`w-full mx-auto grid gap-6 items-stretch
          ${isTwoColumnLayout ? 'grid-cols-1 custom1024:grid-cols-2 max-w-6xl' : 'grid-cols-1 max-w-xl'}
        `}
      >
        {isTwoColumnLayout ? (
          <>
            <div className="order-2 custom1024:order-1 h-full flex flex-col">
              <QuoteCard
                quote={quote}
                fromAsset={fromAsset}
                toAsset={toAsset}
                fromAmount={fromAmount}
                toAmount={toAmount}
                acceptance_time={acceptance_time}
                addressSubmitted={addressSubmitted}
                cryptoAddress={cryptoAddress}
                isExecuted={isExecuted}
              />
            </div>
            <div className="order-3 custom1024:order-2 h-full flex flex-col gap-6">
              {showCryptoAddress && (
                <CryptoAddressCard
                  toAsset={toAsset}
                  cryptoAddress={cryptoAddress}
                  setCryptoAddress={setCryptoAddress}
                  error={error}
                  setError={setError}
                  submitting={submitting}
                  handleAddressSubmit={handleAddressSubmit}
                />
              )}
              {showPaymentInfo && (
                <PaymentInfoCard
                  quote={quote}
                  fromAmount={fromAmount}
                  fromAsset={fromAsset}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {showCryptoAddress && !addressSubmitted && (
              <CryptoAddressCard
                toAsset={toAsset}
                cryptoAddress={cryptoAddress}
                setCryptoAddress={setCryptoAddress}
                error={error}
                setError={setError}
                submitting={submitting}
                handleAddressSubmit={handleAddressSubmit}
              />
            )}
            {showPaymentInfo && (
              <PaymentInfoCard
                quote={quote}
                fromAmount={fromAmount}
                fromAsset={fromAsset}
              />
            )}
            <QuoteCard
              quote={quote}
              fromAsset={fromAsset}
              toAsset={toAsset}
              fromAmount={fromAmount}
              toAmount={toAmount}
              acceptance_time={acceptance_time}
              addressSubmitted={addressSubmitted}
              cryptoAddress={cryptoAddress}
              isExecuted={isExecuted}
            />
          </>
        )}
      </div>
    </div>
  );
}

// Quote Card
interface QuoteCardProps {
  quote: PublicQuote;
  fromAsset: Asset;
  toAsset: Asset;
  fromAmount: number | string | undefined;
  toAmount: number | string | undefined;
  acceptance_time: string | null;
  addressSubmitted: boolean;
  cryptoAddress: string;
  isExecuted: boolean;
}
function QuoteCard({ quote, fromAsset, toAsset, fromAmount, toAmount, acceptance_time, addressSubmitted, cryptoAddress, isExecuted }: QuoteCardProps) {
  return (
    <Card className="w-full bg-[#111827]/90 backdrop-blur-xl shadow-xl rounded-xl md:rounded-2xl overflow-hidden h-full flex flex-col justify-center">
      <CardHeader className="px-4 pt-4 pb-0 md:px-8 md:pt-8">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <CardTitle className="text-lg md:text-2xl font-medium text-white">
            Cotización OTC
          </CardTitle>
          <div className="flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#1C3238] rounded-full">
            <CheckCircle className="h-4 w-4 text-[#26C6DA]" />
            <span className="text-[#26C6DA] text-xs md:text-sm font-medium">
              Aceptada
            </span>
          </div>
        </div>
        <p className="text-sm md:text-md text-gray-400 mt-1">
          para{" "}
          <span className="font-semibold text-gray-200">
            {quote.client_name}
          </span>
        </p>
        {addressSubmitted && (
          <p className="text-xs md:text-sm text-gray-400 mt-1">
            Dirección {quote.base.code}:&nbsp;&nbsp;
            <span className="font-semibold text-gray-200">
              {cryptoAddress}
            </span>
          </p>
        )}
      </CardHeader>
      <CardContent className="px-4 pt-4 flex-1 flex flex-col justify-center md:px-8 md:pt-6">
        <div className="space-y-4">
          {/* Exchange Box Container */}
          <div className="bg-[#0D1526] rounded-xl md:rounded-3xl px-3 py-3 md:px-6 md:py-4 space-y-1 border border-gray-800">
            {/* Entregarás Section */}
            <div className="space-y-1.5">
              <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                Entregarás:
              </span>
              {/* From Asset Box */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="relative">
                      {fromAsset?.image_url && (
                        <img
                          src={fromAsset.image_url}
                          alt={fromAsset.code}
                          className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                        />
                      )}
                    </div>
                    <span className="text-base md:text-xl font-medium text-white">
                      {fromAsset?.code}
                    </span>
                  </div>
                  <div className="text-lg md:text-2xl font-medium text-[#FF6B00] break-all text-center">
                    {formatAssetAmount(fromAmount, fromAsset!)}{" "}
                    {fromAsset?.code}
                  </div>
                </div>
              </motion.div>
            </div>
            {/* Arrow Divider */}
            <div className="flex items-center justify-center pt-2 md:pt-3">
              <div className="h-[1px] flex-1 bg-gray-800"></div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#080B12] p-1.5 md:p-2 rounded-full border border-gray-800/50"
              >
                <ArrowDown className="h-3 w-3 md:h-4 md:w-4 text-gray-500" />
              </motion.div>
              <div className="h-[1px] flex-1 bg-gray-800"></div>
            </div>
            {/* Recibirás Section */}
            <div className="space-y-1.5 pb-1">
              <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                Recibirás:
              </span>
              {/* To Asset Box */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="relative">
                      {toAsset?.image_url && (
                        <img
                          src={toAsset.image_url}
                          alt={toAsset.code}
                          className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                        />
                      )}
                    </div>
                    <span className="text-base md:text-xl font-medium text-white">
                      {toAsset?.code}
                    </span>
                  </div>
                  <div className="text-lg md:text-2xl font-medium text-[#00E676] break-all text-center">
                    {formatAssetAmount(toAmount, toAsset!)}{" "}
                    {toAsset?.code}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          {/* Price Section */}
          <div className="space-y-1.5">
            <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
              Precio:
            </span>
            {/* Price Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50"
            >
              <div className="flex justify-center items-center">
                <span className="text-base md:text-xl font-semibold tracking-wide text-[#FFD700]">
                  1 {quote.base.code} ={" "}
                  {formatAssetAmount(
                    quote.acceptance?.final_price,
                    quote.counter
                  )}{" "}
                  {quote.counter.code}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </CardContent>
      {acceptance_time && (
        <CardFooter className="justify-center px-4 pb-4 pt-3 md:px-8">
          <div className="flex text-[#26C6DA] text-xs md:text-sm">
            <CheckCircle className="h-4 w-4 mr-2" />
            Cotización aceptada el {acceptance_time}
          </div>
        </CardFooter>
      )}
    </Card>
  );
}

// Payment Info Card
interface PaymentInfoCardProps {
  quote: PublicQuote;
  fromAmount: number | string | undefined;
  fromAsset: Asset;
}
function PaymentInfoCard({ quote, fromAmount, fromAsset }: PaymentInfoCardProps) {
  return (
    <Card className="w-full bg-[#111827]/90 backdrop-blur-xl shadow-xl rounded-2xl overflow-hidden h-full flex flex-col justify-center">
      <CardHeader className="px-6 pt-6 pb-0">
        <CardTitle className="text-xl font-medium text-white">
          Instrucciones de pago
        </CardTitle>
      </CardHeader>
      <CardContent className="px-6 pt-4 flex-1 flex flex-col justify-center">
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Banco:</span>
            <span className="text-white font-medium">Banco Azteca</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Beneficiario:</span>
            <span className="text-white font-medium text-right max-w-[60%] break-words">BAX BLOCKCHAIN SERVICES SA DE CV</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Cuenta CLABE:</span>
            <span className="text-white font-medium">127180001501734295</span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Concepto:</span>
              <span className="px-2 py-0.5 bg-red-500/10 border border-red-400/30 rounded-full text-red-300 text-xs font-medium mr-2">
                Importante
              </span>
            </div>
            <span className="text-white font-medium">{quote.slug.slice(-8)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Monto a pagar:</span>
            <span className="text-[#FF6B00] font-medium">
              {formatAssetAmount(fromAmount, fromAsset!)} {fromAsset?.code}
            </span>
          </div>
          <div className="pt-2 border-t border-gray-800 mt-2">
            <p className="text-xs text-gray-400">
              Por favor, asegúrese de incluir el <i><b>concepto</b></i> o <i><b>referencia</b></i> en su transferencia para que podamos procesar su pago de manera eficiente.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Crypto Address Card
interface CryptoAddressCardProps {
  toAsset: Asset;
  cryptoAddress: string;
  setCryptoAddress: (v: string) => void;
  error: string | null;
  setError: (v: string | null) => void;
  submitting: boolean;
  handleAddressSubmit: () => void;
}
function CryptoAddressCard({
  toAsset,
  cryptoAddress,
  setCryptoAddress,
  error,
  setError,
  submitting,
  handleAddressSubmit,
}: CryptoAddressCardProps): JSX.Element {
  return (
    <Card className="bg-[#26C6DA]/10 shadow-2xl rounded-2xl overflow-hidden h-full flex flex-col justify-center border-2 border-[#26C6DA]/40">
      <CardContent className="pt-8 pb-6 flex-1 flex flex-col justify-center">
        <div className="space-y-6">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{toAsset?.code === "MXN" ? "Cuenta de retiro" : "Dirección de retiro"}</h3>
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center space-x-2 bg-red-500/10 border border-red-400/30 rounded-xl p-4"
              >
                <AlertCircle className="h-5 w-5 text-red-300" />
                <p className="text-red-200 text-xs md:text-sm font-medium">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="space-y-2">
            <label htmlFor="crypto-address" className="text-base md:text-lg font-semibold text-white">
              {toAsset?.code === "MXN" ? "Proporcione su cuenta CLABE" : `Proporcione su dirección de ${toAsset?.code}`}:
            </label>
            <Input
              id="crypto-address"
              type="text"
              value={cryptoAddress}
              onChange={(e) => {
                setCryptoAddress(e.target.value);
                if (error) setError(null);
              }}
              placeholder={`Ingrese su ${toAsset?.code === "MXN" ? "cuenta CLABE" : `dirección de ${toAsset?.code}`}`}
              className="bg-[#080B12] text-white border border-gray-800/50 focus:ring-2 focus:ring-[#26C6DA] rounded-2xl p-3 md:p-4 w-full text-base md:text-lg"
              required
            />
          </div>
          <Button
            className={`
              w-full py-4 md:py-5 text-sm md:text-base font-medium rounded-xl border border-[#26C6DA] bg-white text-[#0091EA] hover:bg-[#e0f7fa] hover:text-[#007bb8] transition-all duration-200
              ${submitting ? "bg-gray-700/50 text-gray-400 cursor-not-allowed border-none" : ""}
            `}
            onClick={handleAddressSubmit}
            disabled={submitting}
          >
            {submitting ? "Guardando..." : toAsset?.code === "MXN" ? "Guardar Cuenta" : "Guardar Dirección"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
